import { useState, useCallback } from 'react';
import { DropResult } from '@hello-pangea/dnd';

import { useColumns } from '@/features/cards/context/CardTransactionColumnContext';
import {
  TCardTransactionColumnConfig,
  TCardTransactionColumnKey,
} from '@/features/cards/store/models/card-transactions.model';

export function useCardTransactionSettings() {
  const { columnsConfig, updateColumnConfig } = useColumns();

  const [columns, setColumns] = useState<TCardTransactionColumnConfig>(columnsConfig);
  const [columnOrder, setColumnOrder] = useState(() => Object.keys(columnsConfig));

  const getNumberHiddenData = useCallback(
    () => Object.values(columns).filter((column) => column?.isActive === false).length,
    [columns],
  );

  const handleParentChange = useCallback((key: TCardTransactionColumnKey, isActive: boolean) => {
    setColumns((prevConfig) => ({
      ...prevConfig,
      [key]: {
        ...prevConfig[key],
        isActive,
        children: prevConfig[key].children
          ? Object.entries(prevConfig[key].children!).reduce((acc, [childKey, childValue]) => {
              acc[childKey] = { ...childValue, isActive };
              return acc;
            }, {})
          : prevConfig[key].children,
      },
    }));
  }, []);

  const handleChildChange = useCallback(
    (parentKey: TCardTransactionColumnKey, childKey: TCardTransactionColumnKey, isActive: boolean) => {
      setColumns((prevConfig) => ({
        ...prevConfig,
        [parentKey]: {
          ...prevConfig[parentKey],
          children: {
            ...prevConfig[parentKey].children,
            [childKey]: {
              ...prevConfig[parentKey].children![childKey],
              isActive,
            },
          },
        },
      }));
    },
    [],
  );

  const handleAll = useCallback((isActive: boolean) => {
    setColumns((prevConfig) =>
      Object.entries(prevConfig).reduce((acc, [key, value]) => {
        acc[key] = {
          ...value,
          isActive,
          children: value.children
            ? Object.entries(value.children).reduce((childAcc, [childKey, childValue]) => {
                childAcc[childKey] = { ...childValue, isActive };
                return childAcc;
              }, {})
            : value.children,
        };
        return acc;
      }, {}),
    );
  }, []);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { destination, source } = result;
      if (!destination) {
        return;
      }

      if (destination.droppableId === source.droppableId && destination.index === source.index) {
        return;
      }

      const newOrder = [...columnOrder];
      const [movedItem] = newOrder.splice(source.index, 1);
      newOrder.splice(destination.index, 0, movedItem);

      setColumnOrder(newOrder);
      setColumns((prevColumns) => Object.fromEntries(newOrder.map((key) => [key, prevColumns[key]])));
    },
    [columnOrder],
  );

  const saveSettings = useCallback(() => {
    updateColumnConfig(columns);
  }, [columns, updateColumnConfig]);

  return {
    columns,
    columnOrder,
    getNumberHiddenData,
    handleParentChange,
    handleChildChange,
    handleAll,
    onDragEnd,
    saveSettings,
  };
}
